import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {Id, ISceneBlock, ISceneAddBlock} from "../../../../interfaces";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";
import {faFont, faHeading} from "@fortawesome/free-solid-svg-icons";
import {ContainerService, ImportService, LocalStoreService, SceneAddService, StoreSelectorsService} from "../../../../services";
import {ADDBLOCK_OFFSET, convertBooleanToNumbers, DRAG_ITEM_SIZE, isNullOrUndefOrEmpty, isTruthy} from "../../../../common";
import {Compbaser} from "../../../../components/compbaser";
import {staticSceneStickers} from 'assets/accountTemplates/sticker-constants';
import {Lib} from 'libs/Lib';
import * as _ from "lodash";

@Component({
    selector: 'app-scene-add-sticker',
    templateUrl: './scene-add-sticker.component.html',
    styleUrls: ['./scene-add-sticker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneAddStickerComponent extends Compbaser implements OnInit {

    // @Input() addBlockType: ISceneAddBlock;
    @ViewChild("downloadModal", {read: TemplateRef}) downloadModal: TemplateRef<any>;

    addBlockType: ISceneAddBlock = 'BLOCK_STICKERS';
    filter = '';
    sceneId: Id;
    sceneMime = '';
    hiddenPanel = true;
    stickers = [];
    currentStickers = [];
    chunkedStickers = [];
    stickersAsEmbedded = false;
    resourceName: string;
    activeBlockId;
    blockSmallView = false;
    color = '';
    m_colorChangedFunc;

    throttle = 300;
    scrollDistance = 2;
    initialCount = 30;
    verticalMoreCount = 20;

    readonly dragItemSize = DRAG_ITEM_SIZE;
    readonly faHeading = faHeading;
    readonly faFont = faFont;
    isMouseOnScene = false;

    constructor(
        private translocoService: TranslocoService,
        public storeSelector: StoreSelectorsService,
        private localStoreService: LocalStoreService,
        private containerService: ContainerService,
        protected override dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private toastr: ToastrService,
        private sceneAddService: SceneAddService,
        private importService: ImportService) {
        super();
        this.m_colorChangedFunc = _.debounce(() => {
            this.changeSceneBackgroundColor();
        }, 555);
    }

    ngOnInit() {
        this.cod(
            this.importService.progress$.subscribe(resourceName => {
                this.resourceName = String(resourceName);
                this.cdr.markForCheck();
            })
        );
        this.cod(
            this.containerService.isMouseOnScene.subscribe((v) => {
                this.isMouseOnScene = v;
            })
        );

        this.stickersAsEmbedded = isTruthy(this.localStoreService.getStorageValConfig('stickersAsEmbedded'));
        this.blockSmallView = this.sceneAddService.blockSmallView;
        this.color = this.sceneAddService.color;

        if (this.sceneAddService.addBlockType === 'BLOCK_STICKERS' || this.sceneAddService.addBlockType === 'BLOCK_SNIPPETS') {
            this.addBlockType = this.sceneAddService.addBlockType;
        } else {
            this.sceneAddService.setBlockType(this.addBlockType);
        }
        this.composeStickers();

        setTimeout(() => {
            this.hiddenPanel = false;
            this.cdr.markForCheck();
        }, 500);
    }

    changeBlockType(event) {
        const array: ISceneAddBlock[] = ['BLOCK_STICKERS', 'BLOCK_SNIPPETS'];
        this.addBlockType  = <ISceneAddBlock>array[event.index];
        this.sceneAddService.setBlockType(this.addBlockType);
        this.composeStickers();
    }

    composeStickers() {
        this.stickers = [];
        if (this.addBlockType === 'BLOCK_STICKERS') {
            Object.values(staticSceneStickers).forEach((v) => {
                if (v && v[2]['name'] === 'sticker') {
                    this.stickers.push(v);
                }
            });
        }
        if (this.addBlockType === 'BLOCK_SNIPPETS') {
            Object.values(staticSceneStickers).forEach((v) => {
                if (v && v[2]['name'] === 'text') {
                    this.stickers.push(v);
                }
            });
        }
        this.currentStickers = this.stickers.slice(0, this.initialCount);
        // this.chunkStickers();

        this.cdr.detectChanges();
    }

    /** Load more stickers when scroll down */
    loadMoreStickers() {
        const cnt = this.currentStickers.length;
        if (cnt < this.stickers.length) {
            const moreCount = this.blockSmallView ? (2 * this.verticalMoreCount) : this.verticalMoreCount;
            this.currentStickers.push(...this.stickers.slice(cnt, cnt + moreCount));
        }
        this.cdr.detectChanges();
    }

    addBlock(event) {
        this.sceneAddService.addBlock(event, null, true);
        this.cdr.detectChanges();
    }

    dropBlock(event: CdkDragDrop<any>) {
        if (event.isPointerOverContainer) return;
        this.sceneAddService.dropBlock(event, null, true);
        this.cdr.detectChanges();
    }

    async dropSticker(event: CdkDragDrop<any>) {
        if (event.isPointerOverContainer || !this.isMouseOnScene) return;
        if (this.stickersAsEmbedded) {
            this.activeBlockId = await this.importSticker(event.item.data, true);
            if (!this.activeBlockId) {
                this.toastr.error(this.translocoService.translate('toast.failedImportSticker', {}, this.translocoService.getActiveLang()));
                return;
            }
            this.sceneAddService.setActiveBlockId(this.activeBlockId);
            this.dropBlock(event);
        } else {
            const stickerPlayers = await this.importSticker(event.item.data, false);
            const offset = this.sceneAddService.getOffset(event);
            this.sceneAddService.addScenePlayers(stickerPlayers, offset.offsetX, offset.offsetY, {'locked': 'false'}, false);
        }
    }

    async addSticker(event) {
        if (this.stickersAsEmbedded) {
            this.activeBlockId = await this.importSticker(event, true);
            if (!this.activeBlockId) {
                this.toastr.error(this.translocoService.translate('toast.failedImportSticker', {}, this.translocoService.getActiveLang()));
                return;
            }
            this.sceneAddService.setActiveBlockId(this.activeBlockId);
            this.addBlock(event);
        } else {
            const stickerPlayers: ISceneBlock[] = await this.importSticker(event, false);
            const offset = this.storeSelector.addedBlockCount * ADDBLOCK_OFFSET;
            this.storeSelector.addedBlockCount += 1;
            this.sceneAddService.addScenePlayers(stickerPlayers, offset, offset, {'locked': 'false'}, false);
        }
    }

    addStickerLabel(labelType: 'head' | 'title' | 'label') {
        this.sceneAddService.addHeaderLabel(labelType);
        this.cdr.detectChanges();
    }

    private async importSticker(event, isEmbedded) {
        let res;
        this.resourceName = 'downloading...';

        const dialogRef = this.dialog.open(this.downloadModal, {
            height: '400px',
            width: '400px',
            panelClass: 'transparentDialog',
            data: {},
            disableClose: true
        });

        try {
            if (isEmbedded) {
                const stickerName = event[2].name ? event[2].name : `sticker_${event[0]}_${event[2].sceneId}`;
                const labelColor = !isNullOrUndefOrEmpty(this.color) && this.addBlockType === 'BLOCK_SNIPPETS' ? `${Lib.HexToDecimal(this.color)}` : '';
                res = await this.importService.importScene(event[0], event[2].sceneId, stickerName, true, true, labelColor);
            } else {
                res = await this.importService.importSceneAsPlayers(event[0], event[2].sceneId, true);
            }

        } catch (err) {
            console.log(err);
        } finally {
            setTimeout(() => {
                dialogRef.close();
            }, 1000);
        }
        return res;
    }

    toggleBlockSize() {
        this.blockSmallView = !this.blockSmallView;
        this.sceneAddService.setBlockView(this.blockSmallView);
        if (this.blockSmallView) {
            this.loadMoreStickers();
        }
        this.cdr.detectChanges();
    }

    colorPickerChange(e) {
        this.m_colorChangedFunc();
    }

    toggleStickersAsEmbedded() {
        this.stickersAsEmbedded = !this.stickersAsEmbedded;
        const val = convertBooleanToNumbers(this.stickersAsEmbedded, true);
        this.localStoreService.setStorageValConfig('stickersAsEmbedded', val);
    }

    onClose() {
        this.dialog.closeAll();
    }

    /** Use this function for virtual scrolling. */
    private chunkStickers() {
        const perChunk = 2; // items per chunk
        const result = this.stickers.reduce((resultArray, item, index) => {
            const chunkIndex = Math.floor(index / perChunk);

            if (!resultArray[chunkIndex]) {
                resultArray[chunkIndex] = []; // start a new chunk
            }

            resultArray[chunkIndex].push(item);
            return resultArray;
        }, []);

        this.chunkedStickers = result;
    }

    private changeSceneBackgroundColor() {
        this.sceneAddService.setColor(this.color);
    }

    override destroy() {
        this.sceneAddService.setActiveBlockId(null);
        super.destroy();
    }
}
